if (!customElements.get('timeline-carousel')) {
  customElements.define(
    'timeline-carousel',
    class TimelineCarousel extends HTMLElement {
      constructor() {
        super();
        this.currentSlide = 0;
        this.slides = [];
        this.sliderInstance = null;
        this.debounceTimeout = null;
      }

      connectedCallback() {
        try {
          const slidesData = this.getAttribute('slides-data');
          if (slidesData) {
            this.slides = JSON.parse(slidesData);
          }
        } catch (e) {
          console.error('Error parsing slides data:', e);
          return;
        }

        // Initialize component
        this.render();
        this.initSlider();
        this.initTimelineSlider();
        this.attachEventListeners();
      }

      render() {
        const slidesHtml = this.slides.map((slide, index) => `
          <div class="swiper-slide" data-index="${index}">
            <div class="relative w-full">
              <div class="fact-card-wrapper flex flex-col lg:flex-row items-start justify-between px-12 lg:px-0">
                ${this.renderFactCard(slide.capucine, 'capucine')}
                <div class="fact-card__center flex flex-col gap-2">
                  ${slide.correct_answer.image ? `
                    <img
                      src="${slide.correct_answer.image}"
                      alt="${slide.correct_answer.alt || ''}"
                      class="rounded"
                    >
                  ` : ''}
                  ${slide.correct_answer.title ? `
                    <div class="text-sm">
                      ${slide.correct_answer.title}
                    </div>
                  ` : ''}
                  ${slide.correct_answer.subtitle ? `
                    <div class="text-sm-extra subtitle">
                      ${slide.correct_answer.subtitle}
                    </div>
                  ` : ''}
                </div>
                ${this.renderFactCard(slide.david, 'david')}
              </div>
            </div>
          </div>
        `).join('');

        const timelineMarkers = this.slides.map((slide, index) => {
          const isFirst = index === 0;
          const isLast = index === this.slides.length - 1;
          const showLabel = isFirst || isLast || slide.has_marker;

          return `
            <div class="timeline-marker" data-index="${index}" style="left: ${(index / (this.slides.length - 1)) * 100}%">
              ${showLabel ? `<span class="timeline-marker__label">${slide.year}</span>` : ''}
              ${slide.has_marker ? '<span class="timeline-marker__icon"></span>' : ''}
            </div>
          `;
        }).join('');

        this.innerHTML = `
          <div class="timeline-carousel-wrapper">
            <div class="text-center timeline-year">
              ${this.slides[0]?.year || ''}
            </div>
            <div>
              <div class="timeline-slider-wrapper">
                <input
                  type="range"
                  class="timeline-slider"
                  min="0"
                  max="${this.slides.length - 1}"
                  value="0"
                  step="1"
                >
                <div class="timeline-track">
                  ${timelineMarkers}
                </div>
              </div>
            </div>

            <div class="swiper timeline-swiper">
              <div class="swiper-wrapper">
                ${slidesHtml}
              </div>
            </div>

            <div class="flex justify-between">
              <button class="timeline-nav-btn timeline-nav-prev" ${this.currentSlide === 0 ? 'disabled' : ''}>
                  ${this.currentSlide > 0 ? `
                  <svg class="icon" width="12" height="8" viewBox="0 0 12 8" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.625 3.27539V4.50586H2.77734L5.00391 6.76172L4.125 7.64062L0.375 3.89062L4.125 0.140625L5.00391 1.01953L2.77734 3.27539H11.625Z" fill="currentColor"/>
                  </svg>
                  ` : ''}
                <span class="timeline-nav-text">${this.currentSlide > 0 ? 'Previous' : ''}</span>
              </button>
              <button class="timeline-nav-btn timeline-nav-next" ${this.currentSlide >= this.slides.length - 1 ? 'disabled' : ''}>
                <span class="timeline-nav-text">${this.currentSlide < this.slides.length - 1 ? 'Next' : ''}</span>
                ${this.currentSlide < this.slides.length - 1 ? `
                <svg class="icon" width="12" height="9" viewBox="0 0 12 9" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.5 3.75977H9.34766L7.12109 1.50391L8 0.625L11.75 4.375L8 8.125L7.12109 7.24609L9.34766 4.99023H0.5V3.75977Z" fill="currentColor"/>
                </svg>
                ` : ''}
              </button>
            </div>
          </div>
        `;
      }

      renderFactCard(fact, name) {
        if (!fact) return '';

        const validityClass = fact.validity ? `validity-${fact.validity.toLowerCase().replace(/\s+/g, '-')}` : '';

        return `
          <div class="fact-card flex flex-col ${validityClass}">
            <div class="fact-card__content lg:w-full flex lg:flex-col items-center">
              <div class="flex justify-center items-center flex-col flex-row-reverse">
                ${fact.avatar ? `
                  <div class="fact-card__avatar">
                    <img src="${fact.avatar}" alt="${fact.name}" class="rounded-full">
                  </div>
                ` : ''}
                ${fact.name ? `
                  <div class="fact-card__name text-sm">
                    ${fact.name}
                  </div>
                ` : ''}
              </div>
              <div class="fact-card__pointy ${fact.name === 'Capucine' ? 'mirror-x' : ''}">
                <svg width="30" height="16" viewBox="0 0 30 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0C8.42105 0 17.8947 5.5 17.8947 16C30 9.5 24.7368 0 30 0L0 0Z" fill="#356673"></path>
                </svg>
              </div>
            </div>
            ${fact.statement ? `
              <div class="fact-card__statement">
                <div class="fact-card__statement-content">
                  ${fact.statement}
                    ${fact.validity ? `
                    <div class="fact-card__validity text-sm-extra">
                      ${fact.validity}
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}
          </div>
        `;
      }

      initSlider() {
        const swiperEl = this.querySelector('.timeline-swiper');
        if (!swiperEl) return;

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          adaptiveHeight: true,
          on: {
            slideChange: this.handleSlideChange.bind(this),
          },
        };

        this.sliderInstance = new window.FoxTheme.Carousel(swiperEl, this.sliderOptions);
        this.sliderInstance.init();
      }

      initTimelineSlider() {
        this.timelineSlider = this.querySelector('.timeline-slider');
        if (!this.timelineSlider) return;

        this.timelineSlider.addEventListener('input', (e) => {
          const newIndex = parseInt(e.target.value);
          this.updateSlide(newIndex);
        });
      }

      attachEventListeners() {
        const prevBtn = this.querySelector('.timeline-nav-prev');
        const nextBtn = this.querySelector('.timeline-nav-next');

        if (prevBtn) {
          prevBtn.addEventListener('click', () => {
            if (this.currentSlide > 0) {
              this.goToSlide(this.currentSlide - 1);
            }
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', () => {
            if (this.currentSlide < this.slides.length - 1) {
              this.goToSlide(this.currentSlide + 1);
            }
          });
        }
      }

      handleSlideChange(swiper) {
        const newIndex = swiper.activeIndex;
        this.currentSlide = newIndex;
        this.updateUI();
      }

      updateSlide(newIndex) {
        if (newIndex === this.currentSlide || newIndex < 0 || newIndex >= this.slides.length) {
          return;
        }

        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
          this.goToSlide(newIndex);
        }, 200);
      }

      goToSlide(index) {
        if (!this.sliderInstance || !this.sliderInstance.slider) return;

        this.currentSlide = index;
        this.sliderInstance.slider.slideTo(index);
        this.updateUI();
      }

      updateUI() {
        const yearDisplay = this.querySelector('.timeline-year');
        if (yearDisplay && this.slides[this.currentSlide]) {
          yearDisplay.textContent = this.slides[this.currentSlide].year;
        }

        if (this.timelineSlider) {
          this.timelineSlider.value = this.currentSlide;
        }

        const prevBtn = this.querySelector('.timeline-nav-prev');
        const nextBtn = this.querySelector('.timeline-nav-next');
        const prevText = this.querySelector('.timeline-nav-prev .timeline-nav-text');
        const nextText = this.querySelector('.timeline-nav-next .timeline-nav-text');

        if (prevBtn) {
          const existingArrow = prevBtn.querySelector('.icon');
          if (existingArrow) {
            existingArrow.remove();
          }

          if (this.currentSlide === 0) {
            prevBtn.setAttribute('disabled', '');
            if (prevText) prevText.textContent = '';
          } else {
            prevBtn.removeAttribute('disabled');
            if (prevText) prevText.textContent = 'Previous';

            const arrowSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            arrowSvg.setAttribute('class', 'icon');
            arrowSvg.setAttribute('width', '12');
            arrowSvg.setAttribute('height', '8');
            arrowSvg.setAttribute('viewBox', '0 0 12 8');
            arrowSvg.setAttribute('fill', 'currentColor');

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', 'M11.625 3.27539V4.50586H2.77734L5.00391 6.76172L4.125 7.64062L0.375 3.89062L4.125 0.140625L5.00391 1.01953L2.77734 3.27539H11.625Z');
            path.setAttribute('fill', 'currentColor');

            arrowSvg.appendChild(path);
            prevBtn.insertBefore(arrowSvg, prevText);
          }
        }

        if (nextBtn) {
          const existingArrow = nextBtn.querySelector('.icon');
          if (existingArrow) {
            existingArrow.remove();
          }

          if (this.currentSlide >= this.slides.length - 1) {
            nextBtn.setAttribute('disabled', '');
            if (nextText) nextText.textContent = '';
          } else {
            nextBtn.removeAttribute('disabled');
            if (nextText) nextText.textContent = 'Next';

            const arrowSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            arrowSvg.setAttribute('class', 'icon');
            arrowSvg.setAttribute('width', '12');
            arrowSvg.setAttribute('height', '9');
            arrowSvg.setAttribute('viewBox', '0 0 12 9');
            arrowSvg.setAttribute('fill', 'currentColor');

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', 'M0.5 3.75977H9.34766L7.12109 1.50391L8 0.625L11.75 4.375L8 8.125L7.12109 7.24609L9.34766 4.99023H0.5V3.75977Z');
            path.setAttribute('fill', 'currentColor');

            arrowSvg.appendChild(path);
            nextBtn.appendChild(arrowSvg);
          }
        }
      }

      disconnectedCallback() {
        clearTimeout(this.debounceTimeout);
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy();
        }
      }
    }
  );
}

